import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { projectInit } from 'actions';
import type { OptimisticCommand } from 'components/organisms/AgentStream/types';
import { hydrateProjection } from 'store/reducers/hydration';

export interface StreamTrackerState {
  lastKnownSequence?: number;
  branchId?: string;
  isLoadingBranches: boolean;
  optimisticCommands: OptimisticCommand[];
}

const initialState: StreamTrackerState = {
  optimisticCommands: [],
  isLoadingBranches: true,
};

const streamTrackerSlicer = createSlice({
  name: 'streamTracker',
  initialState,
  reducers: {
    setLastKnownSequence: (
      state,
      action: PayloadAction<{ sequenceId: number | undefined }>,
    ) => {
      state.lastKnownSequence = action.payload.sequenceId;
    },
    setBranchId: (state, action: PayloadAction<{ branchId: string }>) => {
      if (state.branchId !== action.payload.branchId) {
        // When we switch branch, we need to reset the last known sequence
        // so that the projection is hydrated again
        state.lastKnownSequence = undefined;
      }
      state.branchId = action.payload.branchId;
      state.isLoadingBranches = false;
    },
    addOptimisticCommands: (
      state,
      action: PayloadAction<Omit<OptimisticCommand, 'queued'>[]>,
    ) => {
      state.optimisticCommands.push(
        ...action.payload.map(a => ({ ...a, queued: true })),
      );
    },
    removeOptimisticCommandIds: (state, action: PayloadAction<string[]>) => {
      state.optimisticCommands = state.optimisticCommands.filter(
        command => !action.payload.includes(command.commandId),
      );
    },
    clearOptimisticCommands: state => {
      state.optimisticCommands = [];
    },
    clearActionQueue: state => {
      state.optimisticCommands = state.optimisticCommands.map(a => ({
        ...a,
        queued: false,
      }));
    },
  },
  extraReducers: builder => {
    builder.addCase(hydrateProjection, (state, action) => {
      state.lastKnownSequence = action.payload.sequenceId;
    });
    builder.addCase(projectInit, () => {
      return initialState;
    });
  },
});

export const {
  setLastKnownSequence,
  setBranchId,
  addOptimisticCommands,
  removeOptimisticCommandIds,
  clearOptimisticCommands,
  clearActionQueue,
} = streamTrackerSlicer.actions;

export default streamTrackerSlicer.reducer;
